import {createUseStyles} from 'react-jss'
import {useNavigate} from 'react-router-dom'
import {Colors} from '../../constants/colors'
import {CustomerListItemModel} from '../../redux/types'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faBuildingColumns, faExclamationTriangle, faLock, faUsers} from '@fortawesome/free-solid-svg-icons'
import Card from '../Card'
import Pagination from '../Pagination'
import {CompanyModel} from '../../redux/companyMonitorTypes'

export interface Props {
  customersOnPage: CustomerListItemModel[]
  pageIndex: number
  pageSize: number
  totalCount: number
  onPageChange: (pageIndex: number) => void
  companiesWithUnhandledChanges: CompanyModel[]
}

const CustomerList = ({
                        customersOnPage,
                        pageIndex,
                        pageSize,
                        totalCount,
                        onPageChange,
                        companiesWithUnhandledChanges
                      }: Props) => {
  const styles = useStyles()
  const navigate = useNavigate()

  const renderHeader = () => {
    return (
      <Card className={`${styles.container} ${styles.header} card`}>
        <div className={styles.cell}>
          <h6>Name</h6>
        </div>
        <div className={styles.cell}>
          <h6>Company registration number</h6>
        </div>
        <div className={`${styles.cell} ${styles.iconCell}`}>
          <h6>Employer</h6>
        </div>
        <div className={`${styles.cell} ${styles.iconCell}`}>
          <h6>Payout credit facility</h6>
        </div>
        <div className={`${styles.cell} ${styles.iconCell}`}>
          <h6>Changed</h6>
        </div>
        <div className={`${styles.cell} ${styles.iconCell}`}>
          <h6>Locked</h6>
        </div>
      </Card>
    )
  }

  const renderListItems = () => {
    return customersOnPage?.map((item, index) => {
      return (
        <Card
          key={`customer_${index}`}
          className={`${styles.container} ${styles.row} card`}
          onClick={() => navigate(`/customers/${item.id}`)}
        >
          <div className={`${styles.cell} ${getTitleClassName(item)}`}>
            {item.legalName}
          </div>
          <div className={styles.cell}>
            {item.companyRegistrationNumber}
          </div>
          <div className={`${styles.cell} ${styles.iconCell}`}>
            {renderEmployerIndicator(item)}
          </div>
          <div className={`${styles.cell} ${styles.iconCell}`}>
            {renderPayoutCreditFacilityIndicator(item)}
          </div>
          <div className={`${styles.cell} ${styles.iconCell}`}>
            {renderChangedIndicator(item)}
          </div>
          <div className={`${styles.cell} ${styles.iconCell}`}>
            {renderLockedIndicator(item)}
          </div>
        </Card>
      )
    })
  }

  const renderEmployerIndicator = (customerListItem: CustomerListItemModel) => {
    const employer = customerListItem.employer
    if (employer) {
      return (
        <button
          className={styles.iconButton}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/employers/${employer.id}`)
          }}
          title={'View employer'}
        >
          <FontAwesomeIcon
            icon={faUsers}
            color={employer.state === 'active' ? Colors.euro_600 : Colors.darkGray1}
          />
        </button>
      )
    } else {
      return (<span/>)
    }
  }

  const getTitleClassName = (customerListItem: CustomerListItemModel) => {
    switch (customerListItem.state) {
      case 'locked':
        return styles.lockedTitle
      case 'terminated':
        return styles.terminatedTitle
      default:
        return ''
    }
  }

  const renderPayoutCreditFacilityIndicator = (customerListItem: CustomerListItemModel) => {
    const payoutCreditFacility = customerListItem.payoutCreditFacility
    if (payoutCreditFacility) {
      return (
        <button
          className={styles.iconButton}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/payoutcreditfacilities/${payoutCreditFacility.id}`)
          }}
          title={'View payout credit facility'}
        >
          <FontAwesomeIcon
            icon={faBuildingColumns}
            color={payoutCreditFacility.state === 'active' ? Colors.euro_600 : Colors.darkGray1}
          />
        </button>
      )
    } else {
      return (<span/>)
    }
  }

  const renderLockedIndicator = (customerListItem: CustomerListItemModel) => {
    if (customerListItem.state === 'locked') {
      return (<FontAwesomeIcon
        icon={faLock}
        color={Colors.euro_600}
      />)
    } else {
      return (<span/>)
    }
  }

  const renderChangedIndicator = (customerListItem: CustomerListItemModel) => {
    const hasUnhandledChanges = companiesWithUnhandledChanges.some(x =>
      x.marketId === customerListItem?.marketId &&
      x.companyRegistrationNumber === customerListItem.companyRegistrationNumber)

    if (hasUnhandledChanges) {
      return (
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          color={Colors.yellow}
          title='Unhandled changes'
        />
      )
    } else {
      return (<span/>)
    }
  }

  return (
    <div>
      {renderHeader()}
      {renderListItems()}
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={totalCount}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default CustomerList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    padding: '3px 20px',
  },
  header: {
    margin: '30px 0 20px 0',
  },
  row: {
    margin: '10px 0',
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  cell: {
    flex: 1,
  },
  lockedTitle: {
    color: Colors.yen_500
  },
  terminatedTitle: {
    color: Colors.rubel_600
  },
  iconCell: {
    textAlign: 'center',
    flex: '0 0 80px'
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  },
  iconButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)'
    },
    '&:focus': {
      outline: `2px solid ${Colors.euro_600}`,
      outlineOffset: '1px'
    }
  }
})
